import React from 'react';
import AdvancedSearch from './index';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/AdvancedSearch',
  component: AdvancedSearch,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // 1) What problem does it solve?
      `
The **AdvancedSearch** component displays an expandable panel for filtering sites. 
It covers details like location (county, city, state), contact info, visit history, and more.
      `,
      // 2) When & how to use it?
      `
Use **AdvancedSearch** in any scenario where a detailed site search or filtering 
is required. It's especially helpful when users need to narrow down results 
based on multiple criteria such as location, status, or date ranges.
      `,
      // 3) Current Usage
      `
**Sites, Dashboard and Routes Pages**: 
Often rendered as a collapsible/expandable section. 
Users select specific parameters (e.g., county, H2A status, arrival/departure dates) 
and click “Search” to apply them.
      `,
      // 4) Related Components
      `
  - **[DateSelectorV2](#)**: Date picker for arrival, departure, or created date.
  - **[CustomSelect](http://localhost:6006/?path=/docs/components-inputs-customselect--docs)**: Core dropdown used for status, priority, and location.
  - **[AutocompleteText](#)**: For ZIP code entry with suggestion options.
  - **[Button](#)**: Triggers the \`search\` function with the selected filters.
      `,
    ),
  },
  decorators: [(story) => <div style={{ padding: '3rem' }}>{story()}</div>],
  tags: ['autodocs'],
};

const Template = (args) => <AdvancedSearch {...args} />;

export const Default = Template.bind({});
Default.args = {
  sitesParams: {
    county: ['Wake', 'Orange'],
    city: ['Raleigh', 'Morrisville', 'Durham'],
    state: ['North Carolina', 'Virginia', 'California'],
  },
  params: {},
  setParams: () => {},
  search: () => {},
  shouldExpand: false,
};

Default.parameters = generateStoryDocs(
  // When is this variant used?
  `
The **Default** presented in the initial state when a user first opens the panel.
  `,
  // Where is it currently used in the product?
  `
Found on pages with large site lists (e.g., Dashboard), allowing a user 
to open AdvancedSearch on demand and apply fresh filters.
  `,
);

export const WithSelectedOptions = Template.bind({});
WithSelectedOptions.args = {
  ...Default.args,
  shouldExpand: true,
  params: {
    county: 'Wake',
    zip_code: '27519',
    arrival: '4/30/24',
    departure: '6/30/24',
    status: 'Active',
  },
};

WithSelectedOptions.parameters = generateStoryDocs(
  // When is this variant used?
  `
The **WithSelectedOptions** showcases a scenario where certain parameters 
are already set.
  `,
  // Where is it currently used in the product?
  `
Displayed if the user has selected parameters previously and it remains upon reloading the page.
  `,
);

